import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type IconName =
  | 'dashboard'
  | 'plane'
  | 'apps'
  | 'chart'
  | 'settings'
  | 'monitor'
  | 'plug'
  | 'help'
  | 'arrow-up-right'
  | 'arrow-right'
  | 'arrow-left'
  | 'user-cog'
  | 'logout'
  | 'panel'
  | 'chevrons'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-left'
  | 'chevron-right'
  | 'search'
  | 'copy'
  | 'eye'
  | 'eye-off'
  | 'calendar'
  | 'download'
  | 'upload'
  | 'ticket'
  | 'users'
  | 'building'
  | 'info'
  | 'check'
  | 'x'
  | 'plus'
  | 'rotate'
  | 'lock'
  | 'sort'
  | 'list-checks';

/**
 * Lucide icons (https://lucide.dev, ISC) inlined as SVG so the portal matches the AGW Developer
 * Portal without pulling in an icon font. Size comes from the host class: default 16px, `sm` 12px,
 * `lg` 20px (see styles.scss). Decorative by default; pass `label` to expose it to screen readers.
 */
@Component({
  selector: 'app-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      viewBox="0 0 24 24"
      [attr.aria-hidden]="label() ? null : 'true'"
      [attr.aria-label]="label() || null"
      [attr.role]="label() ? 'img' : null"
    >
      @switch (name()) {
        @case ('dashboard') {
          <svg:rect width="7" height="9" x="3" y="3" rx="1" />
          <svg:rect width="7" height="5" x="14" y="3" rx="1" />
          <svg:rect width="7" height="9" x="14" y="12" rx="1" />
          <svg:rect width="7" height="5" x="3" y="16" rx="1" />
        }
        @case ('plane') {
          <svg:path
            d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"
          />
        }
        @case ('apps') {
          <svg:rect width="7" height="7" x="3" y="3" rx="1" />
          <svg:rect width="7" height="7" x="14" y="3" rx="1" />
          <svg:rect width="7" height="7" x="14" y="14" rx="1" />
          <svg:rect width="7" height="7" x="3" y="14" rx="1" />
        }
        @case ('chart') {
          <svg:path d="M3 3v18h18" />
          <svg:path d="M18 17V9" />
          <svg:path d="M13 17V5" />
          <svg:path d="M8 17v-3" />
        }
        @case ('settings') {
          <svg:path
            d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
          />
          <svg:circle cx="12" cy="12" r="3" />
        }
        @case ('monitor') {
          <svg:rect width="20" height="14" x="2" y="3" rx="2" />
          <svg:line x1="8" x2="16" y1="21" y2="21" />
          <svg:line x1="12" x2="12" y1="17" y2="21" />
        }
        @case ('plug') {
          <svg:path d="M12 22v-5" />
          <svg:path d="M9 8V2" />
          <svg:path d="M15 8V2" />
          <svg:path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
        }
        @case ('help') {
          <svg:circle cx="12" cy="12" r="10" />
          <svg:path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <svg:path d="M12 17h.01" />
        }
        @case ('arrow-up-right') {
          <svg:path d="M7 7h10v10" />
          <svg:path d="M7 17 17 7" />
        }
        @case ('arrow-right') {
          <svg:path d="M5 12h14" />
          <svg:path d="m12 5 7 7-7 7" />
        }
        @case ('arrow-left') {
          <svg:path d="m12 19-7-7 7-7" />
          <svg:path d="M19 12H5" />
        }
        @case ('user-cog') {
          <svg:circle cx="18" cy="15" r="3" />
          <svg:circle cx="9" cy="7" r="4" />
          <svg:path d="M10 15H6a4 4 0 0 0-4 4v2" />
          <svg:path d="m21.7 16.4-.9-.3" />
          <svg:path d="m15.2 13.9-.9-.3" />
          <svg:path d="m16.6 18.7.3-.9" />
          <svg:path d="m19.1 12.2.3-.9" />
          <svg:path d="m19.6 18.7-.4-1" />
          <svg:path d="m16.8 12.3-.4-1" />
          <svg:path d="m14.3 16.6 1-.4" />
          <svg:path d="m20.7 13.8 1-.4" />
        }
        @case ('logout') {
          <svg:path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <svg:polyline points="16 17 21 12 16 7" />
          <svg:line x1="21" x2="9" y1="12" y2="12" />
        }
        @case ('panel') {
          <svg:rect width="18" height="18" x="3" y="3" rx="2" />
          <svg:path d="M9 3v18" />
        }
        @case ('chevrons') {
          <svg:path d="m7 15 5 5 5-5" />
          <svg:path d="m7 9 5-5 5 5" />
        }
        @case ('chevron-down') {
          <svg:path d="m6 9 6 6 6-6" />
        }
        @case ('chevron-up') {
          <svg:path d="m18 15-6-6-6 6" />
        }
        @case ('chevron-left') {
          <svg:path d="m15 18-6-6 6-6" />
        }
        @case ('chevron-right') {
          <svg:path d="m9 18 6-6-6-6" />
        }
        @case ('search') {
          <svg:circle cx="11" cy="11" r="8" />
          <svg:path d="m21 21-4.3-4.3" />
        }
        @case ('copy') {
          <svg:rect width="14" height="14" x="8" y="8" rx="2" />
          <svg:path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
        }
        @case ('eye') {
          <svg:path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <svg:circle cx="12" cy="12" r="3" />
        }
        @case ('eye-off') {
          <svg:path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
          <svg:path
            d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
          />
          <svg:path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
          <svg:line x1="2" x2="22" y1="2" y2="22" />
        }
        @case ('calendar') {
          <svg:rect width="18" height="18" x="3" y="4" rx="2" />
          <svg:path d="M16 2v4" />
          <svg:path d="M8 2v4" />
          <svg:path d="M3 10h18" />
        }
        @case ('download') {
          <svg:path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <svg:polyline points="7 10 12 15 17 10" />
          <svg:line x1="12" x2="12" y1="15" y2="3" />
        }
        @case ('upload') {
          <svg:path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <svg:polyline points="17 8 12 3 7 8" />
          <svg:line x1="12" x2="12" y1="3" y2="15" />
        }
        @case ('ticket') {
          <svg:path
            d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"
          />
          <svg:path d="m9 12 2 2 4-4" />
        }
        @case ('users') {
          <svg:path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <svg:circle cx="9" cy="7" r="4" />
          <svg:path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <svg:path d="M16 3.13a4 4 0 0 1 0 7.75" />
        }
        @case ('building') {
          <svg:path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <svg:path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
          <svg:path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
          <svg:path d="M10 6h4" />
          <svg:path d="M10 10h4" />
          <svg:path d="M10 14h4" />
          <svg:path d="M10 18h4" />
        }
        @case ('info') {
          <svg:circle cx="12" cy="12" r="10" />
          <svg:path d="M12 16v-4" />
          <svg:path d="M12 8h.01" />
        }
        @case ('check') {
          <svg:path d="M20 6 9 17l-5-5" />
        }
        @case ('x') {
          <svg:path d="M18 6 6 18" />
          <svg:path d="m6 6 12 12" />
        }
        @case ('plus') {
          <svg:path d="M5 12h14" />
          <svg:path d="M12 5v14" />
        }
        @case ('rotate') {
          <svg:path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <svg:path d="M3 3v5h5" />
        }
        @case ('lock') {
          <svg:rect width="18" height="11" x="3" y="11" rx="2" />
          <svg:path d="M7 11V7a5 5 0 0 1 10 0v4" />
        }
        @case ('list-checks') {
          <svg:path d="m3 17 2 2 4-4" />
          <svg:path d="m3 7 2 2 4-4" />
          <svg:path d="M13 6h8" />
          <svg:path d="M13 12h8" />
          <svg:path d="M13 18h8" />
        }
        @case ('sort') {
          <svg:path d="m21 16-4 4-4-4" />
          <svg:path d="M17 20V4" />
          <svg:path d="m3 8 4-4 4 4" />
          <svg:path d="M7 4v16" />
        }
      }
    </svg>
  `,
})
export class IconComponent {
  readonly name = input.required<IconName>();
  readonly label = input<string>('');
}
